// ---------------------------------------------------------------------------
// What the Settings page's flows are handed (R18b)
//
// The flows of the Settings page live in modules of their own, without Lit,
// Adornis or the page's state (REDESIGN-PLAN R18b):
//
//   settings-load.ts      the page's lists, the two kinds of mailbox in one
//   settings-reps.ts      saving a Sales Rep, creating one for a connect,
//                         removing one a connect left without an account
//   settings-accounts.ts  connecting a mailbox or a LinkedIn account through
//                         its sign-in popup, assigning it, the warm-up switch
//   settings-crm.ts       connecting HubSpot, disconnecting a CRM, the Lead
//                         Status mapping
//   settings-trash.ts     the recycle bin: its list, restore, delete for good
//
// (Deleting a rep and disconnecting an account ask first; they are R18a's,
// in settings-safeguards.ts.) Each flow is handed what it talks to — the
// server, the popup, the clock, the toast — and the page's setters, so it is
// tested with stand-ins (tests/settings-*.test.ts) and the page only wires
// it. The defaults below are the browser's own, looked up when called, not
// when this module loads, so a stand-in installed later is the one used.
// ---------------------------------------------------------------------------

/** fetch, or a stand-in for it. */
export type Send = (url: string, init?: RequestInit) => Promise<Response>;

/** How the page tells the person: its toast, and for an error the box above the panels. */
export interface Say {
  success(message: string): void;
  error(message: string): void;
  warning(message: string): void;
}

/** The timers a flow polls with, and the time. */
export interface Timers {
  setInterval(fn: () => unknown, ms: number): unknown;
  clearInterval(id: unknown): void;
  setTimeout(fn: () => unknown, ms: number): unknown;
  clearTimeout(id: unknown): void;
  now(): number;
}

/** The browser's fetch, as it is when called. */
export const viaFetch: Send = (url, init) => (init === undefined ? fetch(url) : fetch(url, init));

/** The browser's timers, as they are when called. */
export const browserTimers: Timers = {
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: id => clearInterval(id as ReturnType<typeof setInterval>),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: id => clearTimeout(id as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

/** The prefix of every line the Settings flows write to the console: the client has no telemetry of its own. */
export const LOG_PREFIX = '[settings]';

/** Writes a line a recurrence can be seen by, as console.warn is when called. */
export const warn = (event: string, detail: Record<string, unknown>) => console.warn(`${LOG_PREFIX} ${event}`, detail);

/** A JSON answer, or `{ ok: false }` when the request failed or the answer was not JSON. */
export async function answerOf(request: Promise<Response>): Promise<Record<string, any>> {
  return request.then(r => r.json()).catch(() => ({ ok: false }));
}
